/**
 * Prufe Cognito Authentication Service
 *
 * Wraps amazon-cognito-identity-js for all auth operations.
 * Handles sign-up, sign-in, MFA, password reset, and token management.
 */

import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
  type ISignUpResult,
} from 'amazon-cognito-identity-js';

// --- Configuration ---

const POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || 'eu-west-1_sPYbFmSNJ';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '7ma4o0drajih6ei4vdh2thavca';

let userPool: CognitoUserPool | null = null;

export function configure(): CognitoUserPool {
  if (userPool) return userPool;
  userPool = new CognitoUserPool({
    UserPoolId: POOL_ID,
    ClientId: CLIENT_ID,
  });
  return userPool;
}

function getPool(): CognitoUserPool {
  if (!userPool) configure();
  return userPool!;
}

function makeCognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: getPool() });
}

// --- Token parsing ---

export interface ParsedIdToken {
  email: string;
  tenantId: string | null;
  accountId: string | null;
  role: string | null;
  exp: number;
  sub: string;
}

export function parseIdToken(token: string): ParsedIdToken {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT');
  const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  return {
    email: payload.email ?? '',
    tenantId: payload['custom:tenantId'] ?? null,
    accountId: payload['custom:accountId'] ?? null,
    role: payload['custom:role'] ?? null,
    exp: payload.exp ?? 0,
    sub: payload.sub ?? '',
  };
}

// --- Sign Up ---

export function signUp(
  email: string,
  password: string,
  tenantName: string,
): Promise<ISignUpResult> {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
    ];
    getPool().signUp(email, password, attributes, [], (err, result) => {
      if (err) return reject(err);
      resolve(result!);
    });
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.confirmRegistration(code, true, (err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.resendConfirmationCode((err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

// --- Sign In ---

export interface SignInResult {
  session: CognitoUserSession;
  cognitoUser: CognitoUser;
}

export interface MfaChallengeResult {
  challengeName: 'SOFTWARE_TOKEN_MFA';
  cognitoUser: CognitoUser;
}

export function signIn(
  email: string,
  password: string,
): Promise<SignInResult | MfaChallengeResult> {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ session, cognitoUser: user }),
      onFailure: (err) => reject(err),
      totpRequired: () => {
        resolve({ challengeName: 'SOFTWARE_TOKEN_MFA', cognitoUser: user });
      },
      mfaRequired: () => {
        resolve({ challengeName: 'SOFTWARE_TOKEN_MFA', cognitoUser: user });
      },
    });
  });
}

export function handleMfaChallenge(
  code: string,
  cognitoUser: CognitoUser,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
      },
      'SOFTWARE_TOKEN_MFA',
    );
  });
}

// --- MFA Setup ---

export function setupMfa(cognitoUser: CognitoUser): Promise<string> {
  return new Promise((resolve, reject) => {
    cognitoUser.associateSoftwareToken({
      associateSecretCode: (secretCode) => resolve(secretCode),
      onFailure: (err) => reject(err),
    });
  });
}

export function verifyMfaSetup(
  code: string,
  cognitoUser: CognitoUser,
): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(code, 'Prufe TOTP', {
      onSuccess: () => {
        // Set MFA preference to TOTP
        cognitoUser.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err) => {
          if (err) return reject(err);
          resolve();
        });
      },
      onFailure: (err) => reject(err),
    });
  });
}

// --- Session Management ---

export function getCurrentUser(): CognitoUser | null {
  return getPool().getCurrentUser();
}

export function getSession(): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error('No current user'));
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) return reject(err);
      if (!session || !session.isValid()) return reject(new Error('Session invalid'));
      resolve(session);
    });
  });
}

export function refreshSession(): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error('No current user'));
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) return reject(err);
      if (!session) return reject(new Error('No session'));
      const refreshToken = session.getRefreshToken();
      user.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (refreshErr) return reject(refreshErr);
        resolve(newSession);
      });
    });
  });
}

export function signOut(): void {
  const user = getCurrentUser();
  if (user) user.signOut();
}

// --- Password Management ---

export function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error('No current user'));
    user.getSession((err: Error | null) => {
      if (err) return reject(err);
      user.changePassword(oldPassword, newPassword, (changeErr) => {
        if (changeErr) return reject(changeErr);
        resolve();
      });
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}
